import { FC, useEffect, useRef, useState } from "react";
import { useUserSettings } from "../../context/userSettingsContext";
import { useI18n } from "../../hooks/useTranslations";
import { useRolesApi } from "../../http/roles/api";
import { Role } from "../../http/roles/types";
import { User } from "../../http/users/types";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import MultiCheckboxPermission from "../ui/MultiCheckboxPermission";
import MultiSelect from "../ui/MultiSelect";
import SubsidiariesSelector from "../ui/SubsidiariesSelector";
import styles from "./RolesAndPermissionsUserForm.module.css";

export interface RoleAndPermissionsUserFormBody {
  roles: Role[];
  permissions: string[];
  userSubsidiariesIDs: string[];
}

type RoleAndPermissionsUserFormProps = {
  onSubmit: (form: RoleAndPermissionsUserFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  user?: User | null;
};

const RoleAndPermissionsUserForm: FC<RoleAndPermissionsUserFormProps> = ({
  onSubmit,
  isDisabled,
  isLoading,
  user,
}: RoleAndPermissionsUserFormProps) => {
  const i18n = useI18n();
  const rolesApi = useRolesApi();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();

  const dataRoles = rolesApi.useAdminRoles({}, aborter.current.signal);
  const [totalSubsidiaries, setTotalSubsidiaries] = useState(0);

  const defaultValues = {
    roles: user?.roles ?? [],
    permissions: user?.permissions ?? [],
    userSubsidiariesIDs: user?.userSubsidiariesIDs ?? [],
  };

  return (
    <>
      <Form<RoleAndPermissionsUserFormBody>
        submitText={i18n.t.common.save}
        disabled={isDisabled}
        onSubmit={() => onSubmit}
        defaultValues={defaultValues}
      >
        {({ setValue, watch, reset }) => {
          const permissionsSelected = watch("permissions");
          const selectedRoles = watch("roles");

          useEffect(() => {
            reset(defaultValues);
          }, [user]);

          const isCheckBoxDisabled = (permissions: string[], permissionPrefix: string, permissionAction: string) => {
            const permission = permissionPrefix + ":" + permissionAction;
            return (
              !permissions.some((p) => p === permissionAction) ||
              selectedRoles.some((r) => r.permissions?.some((p) => p === permission)) ||
              (permissionAction === "read" &&
                permissionsSelected.some(
                  (p) => p.split(":")[0] === permissionPrefix && p !== permissionPrefix + ":read",
                ))
            );
          };

          const onPermissionRowClick = (permissions: string[], resource: string) => {
            const rowPermissions = ["read", "insert", "update", "delete"].map((op) => `${resource}:${op}`);

            const hasAny = rowPermissions.some((perm) => permissionsSelected.includes(perm));

            if (hasAny) {
              setValue(
                "permissions",
                permissionsSelected.filter((p) => p.split(":")[0] !== resource),
              );
            } else {
              let rolesToAdd: string[] = [];
              rowPermissions.forEach((permission) => {
                !isCheckBoxDisabled(permissions, permission.split(":")[0] ?? "", permission.split(":")[1] ?? "") &&
                  rolesToAdd.push(permission);
              });
              setValue("permissions", [...permissionsSelected, ...rolesToAdd]);
            }
          };

          const isCheckBoxChecked = (permission: string) => {
            return (
              permissionsSelected.includes(permission) ||
              selectedRoles.some((r) => r.permissions?.some((p) => p === permission))
            );
          };

          const onCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>, permission: string) => {
            const newPermissions = e.target.checked
              ? [...permissionsSelected, permission]
              : permissionsSelected.filter((p) => p !== permission);

            if (permission.split(":")[1] !== "read") {
              !newPermissions.find((p) => permission.split(":")[0] + ":read" === p) &&
                newPermissions.push(permission.split(":")[0] + ":read");
            }

            setValue("permissions", newPermissions);
          };

          return (
            <div className={styles.form}>
              <FormFieldsContainer>
                {user?.department && (
                  <FormField label={i18n.t.common.department} isOptional>
                    <Input value={user.department} readOnly />
                  </FormField>
                )}
                <FormField label={i18n.t.common.roles} isOptional>
                  <MultiSelect
                    values={
                      dataRoles.data?.items
                        ? dataRoles.data?.items.map((x) => ({
                            id: x.id,
                            name: x.name,
                            selected: selectedRoles.some((s) => x.id == s.id),
                            disabled: isDisabled || x.departments?.some((d) => d === user?.department),
                          }))
                        : []
                    }
                    onUpdate={(id, checked) => {
                      const role = dataRoles?.data?.items.find((r) => r.id === id);
                      if (!role) return;

                      const newRoles = checked
                        ? [...(selectedRoles ?? []), role]
                        : (selectedRoles ?? []).filter((r) => r.id !== id);

                      setValue("roles", newRoles);

                      let updatedPermissions = [...(permissionsSelected ?? [])];

                      if (checked) {
                        role.permissions?.forEach((perm) => {
                          if (!updatedPermissions.includes(perm)) {
                            updatedPermissions.push(perm);
                          }
                        });
                      } else {
                        const remainingRolePerms = newRoles.flatMap((r) => r.permissions ?? []);

                        updatedPermissions = updatedPermissions.filter(
                          (perm) => !role.permissions?.includes(perm) || remainingRolePerms.includes(perm),
                        );
                      }

                      setValue("permissions", updatedPermissions);
                    }}
                    selectedItems={selectedRoles.map((x) => x.id)}
                    disabled={true}
                  />
                </FormField>

                <FormField label={i18n.t.permissions.plural} isOptional>
                  <MultiCheckboxPermission
                    isDisabled={isDisabled || isLoading}
                    onPermissionRowClick={onPermissionRowClick}
                    isCheckBoxDisabled={isCheckBoxDisabled}
                    isCheckBoxChecked={isCheckBoxChecked}
                    onCheckBoxChange={onCheckBoxChange}
                    showClients={userSettings.permissions.clients.read}
                  />
                </FormField>
              </FormFieldsContainer>
              <FormFieldsContainer>
                <FormField
                  label={`${i18n.t.subsidiaries.plural} (${watch("userSubsidiariesIDs")?.length || 0}/${totalSubsidiaries})`}
                  isOptional
                >
                  <SubsidiariesSelector
                    isDisabled={!userSettings.permissions.userRoles.update || isDisabled}
                    selectedSubsidiaries={watch("userSubsidiariesIDs")}
                    onSubsidiaryClick={(ids, total) => {
                      setValue("userSubsidiariesIDs", ids);
                      setTotalSubsidiaries(total);
                    }}
                  />
                </FormField>
              </FormFieldsContainer>
            </div>
          );
        }}
      </Form>
    </>
  );
};

export default RoleAndPermissionsUserForm;
